import React from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { FavoriteBorderRounded, FavoriteRounded } from "@mui/icons-material";

import SideDrawer from "../../../../../components/ui/SideDrawer";
import { HexTile } from "../../../../../components/ui/Hexagon";
import StatusBadge from "../../../../../components/ui/StatusBadge";
import getDescriptionPeriod from "../../../../../services/periodService";
import { getPeriodLabel } from "../../../../../utils/period-label";
import { Details } from "./product-detail";

// Drawer lateral de detalhe do ativo no wizard (fiel ao handoff: hexágono,
// chips, seções e cards creme). Substitui o dialog central antigo, que ficava
// ilegível no tema escuro. O <ProductDetail> compartilhado segue intacto.

type Props = {
    open: boolean
    onClose: () => void
    detail: Details
    onFavorite?: (productId: number, isFavorite: boolean) => void
    /** Rótulo do tipo no badge do header (default "Tópico") */
    typeLabel?: string
}

function SectionLabel({ children }: { children: string }) {
    return (
        <Typography sx={{ mb: 1, fontSize: "11px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "primary.main" }}>
            {children}
        </Typography>
    );
}

function SectionText({ children }: { children: React.ReactNode }) {
    return <Typography sx={{ fontSize: "14px", lineHeight: 1.7, color: "text.secondary" }}>{children}</Typography>;
}

function StatCard({ label, value, valueColor }: { label: string; value: string; valueColor: string }) {
    const theme = useTheme();
    return (
        <Box sx={{ backgroundColor: theme.palette.brand.surface2, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px", p: "14px 16px" }}>
            <Typography sx={{ mb: "5px", fontSize: "10.5px", fontWeight: 600, letterSpacing: "1px", textTransform: "uppercase", color: "text.secondary" }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: valueColor }}>{value}</Typography>
        </Box>
    );
}

export const ProductDetailDrawer: React.FC<Props> = ({ open, onClose, detail, onFavorite, typeLabel = "Tópico" }) => {
    const theme = useTheme();

    const unit = detail.unity ?? "%";
    const conc = detail.showConcentration
        ? [detail.minimum, detail.maximum].filter((v) => v != null && `${v}` !== "").map((v) => `${v}${unit}`).join(" – ")
        : null;

    const incompatibility =
        detail.incompatibility && detail.incompatibility !== "NENHUM" && detail.incompatibility !== "none"
            ? detail.incompatibility
            : null;

    const flags: string[] = [];
    if (detail.daytime) flags.push("Diurno");
    if (detail.nighttime) flags.push("Noturno");
    if (detail.vegan) flags.push("Vegano");
    if (detail.organic) flags.push("Orgânico");
    if (detail.pregnant) flags.push("Permitido na gestação");

    const period = getPeriodLabel(getDescriptionPeriod(detail.daytime, detail.nighttime));

    return (
        <SideDrawer
            open={open}
            onClose={onClose}
            header={
                <Box sx={{ width: "100%", minWidth: 0 }}>
                    <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start", width: "100%", minWidth: 0 }}>
                        <Box sx={{ flexShrink: 0 }}>
                            <HexTile label={detail.name ?? ""} size={48} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                            <Typography
                                sx={{
                                    fontFamily: theme.typography.fontFamilyDisplay,
                                    fontSize: { xs: 22, sm: 25 },
                                    lineHeight: 1.15,
                                    color: "text.primary",
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 3,
                                    overflow: "hidden",
                                    overflowWrap: "break-word",
                                    wordBreak: "break-word",
                                }}
                            >
                                {detail.name}
                            </Typography>
                            <Box sx={{ mt: "6px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                <StatusBadge status={typeLabel} tone="neutral" />
                                {detail.classes && <StatusBadge status={detail.classes} tone="neutral" />}
                            </Box>
                        </Box>
                    </Box>
                    {!!onFavorite && (
                        <Box sx={{ mt: "18px", display: "flex", gap: 1.25 }}>
                            <Button
                                fullWidth
                                onClick={() => onFavorite(detail.id, detail.favorite)}
                                startIcon={detail.favorite ? <FavoriteRounded sx={{ color: "primary.main" }} /> : <FavoriteBorderRounded />}
                                sx={{ height: 42, border: `1.5px solid ${theme.palette.divider}`, color: "secondary.main" }}
                            >
                                {detail.favorite ? "Favorito" : "Favoritar"}
                            </Button>
                        </Box>
                    )}
                </Box>
            }
            footer={
                <Button fullWidth variant="outlined" color="secondary" onClick={onClose} sx={{ height: 48 }}>
                    Fechar
                </Button>
            }
        >
            {detail.actives && detail.actives.length > 0 && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Classes</SectionLabel>
                    <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                        {detail.actives.map((a) => (
                            <Chip key={a} label={a} size="small" sx={{ backgroundColor: theme.palette.brand.cream, color: "primary.dark", fontWeight: 600 }} />
                        ))}
                    </Box>
                </Box>
            )}

            {detail.mechanism && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Mecanismo de ação</SectionLabel>
                    <SectionText>{detail.mechanism}</SectionText>
                </Box>
            )}
            {detail.description && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Indicações</SectionLabel>
                    <SectionText>{detail.description}</SectionText>
                </Box>
            )}

            {(conc || detail.ph) && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: "22px" }}>
                    {conc && <StatCard label="Concentração usual" value={conc} valueColor="primary.main" />}
                    {detail.ph && <StatCard label="pH de estabilidade" value={detail.ph} valueColor="text.primary" />}
                </Box>
            )}

            {incompatibility && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Incompatibilidades</SectionLabel>
                    <SectionText>{incompatibility}</SectionText>
                </Box>
            )}

            {flags.length > 0 && (
                <Box sx={{ mb: "22px", display: "flex", gap: "6px", flexWrap: "wrap" }}>
                    {flags.map((flag) => (
                        <Chip key={flag} label={flag} size="small" sx={{ backgroundColor: theme.palette.brand.cream, color: "primary.dark", fontWeight: 600 }} />
                    ))}
                </Box>
            )}

            {period && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Período</SectionLabel>
                    <SectionText>{period}</SectionText>
                </Box>
            )}

            {detail.source && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Fonte</SectionLabel>
                    <SectionText>{detail.source}</SectionText>
                </Box>
            )}

            {detail.annotation && (
                <Box sx={{ mb: "22px" }}>
                    <SectionLabel>Minha anotação</SectionLabel>
                    <SectionText>{detail.annotation}</SectionText>
                </Box>
            )}
        </SideDrawer>
    )
}
